/**
 * Build-time and runtime chrome theming for customer iframe builds.
 * Overrides Bootstrap CSS variables on documentElement / injected stylesheets.
 * Does not affect Konva timeline palettes.
 */

const SYSTEM_THEME_LINK_ID = "cc-system-theme";
const CUSTOM_THEME_STYLE_ID = "cc-custom-theme";
const APPLIED_TOKEN_ATTR = "data-cc-theme-tokens";

/** Short aliases parents can pass to setTheme() → CSS custom properties */
const TOKEN_ALIASES = {
	primary: "--bs-primary",
	primaryRgb: "--bs-primary-rgb",
	bodyBg: "--bs-body-bg",
	bodyBgRgb: "--bs-body-bg-rgb",
	bodyColor: "--bs-body-color",
	bodyColorRgb: "--bs-body-color-rgb",
	secondaryColor: "--bs-secondary-color",
	borderColor: "--bs-border-color",
	fontFamily: "--bs-body-font-family",
	linkColor: "--bs-link-color",
	linkHoverColor: "--bs-link-hover-color",
	light: "--bs-light",
	danger: "--bs-danger",
	warning: "--bs-warning",
	success: "--bs-success",
	info: "--bs-info",
};

/** @type {string[]} */
let appliedTokenKeys = [];

/**
 * @param {string} value
 * @returns {string | null} "r, g, b" or null
 */
function hexToRgbTriplet(value) {
	if (typeof value !== "string") return null;
	const hex = value.trim().replace(/^#/, "");
	if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(hex)) return null;
	const full =
		hex.length === 3
			? hex
					.split("")
					.map((c) => c + c)
					.join("")
			: hex;
	const n = parseInt(full, 16);
	return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

/**
 * Resolve alias or raw CSS variable name to a --custom-property.
 * @param {string} key
 * @returns {string | null}
 */
function resolveTokenKey(key) {
	if (typeof key !== "string" || !key) return null;
	if (key.startsWith("--")) return key;
	return TOKEN_ALIASES[key] ?? null;
}

/**
 * Load per-system theme stylesheet (mirrors logo path pattern).
 * Skips demo. Missing files 404 harmlessly.
 * @param {string} system
 */
export function loadSystemTheme(system) {
	if (typeof document === "undefined") return;
	if (!system || system === "demo") {
		const existing = document.getElementById(SYSTEM_THEME_LINK_ID);
		if (existing) existing.remove();
		return;
	}

	const href = `./assets/css/themes/${system}.css`;
	let link = document.getElementById(SYSTEM_THEME_LINK_ID);
	if (!link) {
		link = document.createElement("link");
		link.id = SYSTEM_THEME_LINK_ID;
		link.rel = "stylesheet";
		document.head.appendChild(link);
	}
	if (link.getAttribute("href") !== href) {
		link.setAttribute("href", href);
	}
}

/**
 * Inject or replace runtime custom CSS from the parent host.
 * @param {string} cssText
 */
export function applyThemeCss(cssText) {
	if (typeof document === "undefined") return;
	if (typeof cssText !== "string") {
		throw new Error("cssText must be a string.");
	}

	let style = document.getElementById(CUSTOM_THEME_STYLE_ID);
	if (!style) {
		style = document.createElement("style");
		style.id = CUSTOM_THEME_STYLE_ID;
		document.head.appendChild(style);
	}
	style.textContent = cssText;
}

/**
 * Apply structured theme tokens as CSS variables on documentElement.
 * Accepts aliases (primary, bodyBg, …) or raw --bs-* names.
 * When primary is a hex color and primaryRgb is omitted, rgb is derived.
 *
 * @param {Record<string, string | number | null | undefined>} tokens
 */
export function applyThemeTokens(tokens) {
	if (typeof document === "undefined") return;
	if (!tokens || typeof tokens !== "object" || Array.isArray(tokens)) {
		throw new Error("tokens must be a plain object.");
	}

	const root = document.documentElement;
	const next = { ...tokens };

	if (next.primary != null && next.primaryRgb == null && next["--bs-primary-rgb"] == null) {
		const rgb = hexToRgbTriplet(String(next.primary));
		if (rgb) next.primaryRgb = rgb;
	}

	for (const [key, value] of Object.entries(next)) {
		const cssVar = resolveTokenKey(key);
		if (!cssVar) {
			console.warn(`[themeLoader] Unknown theme token ignored: ${key}`);
			continue;
		}
		if (value === null || value === undefined || value === "") {
			root.style.removeProperty(cssVar);
			appliedTokenKeys = appliedTokenKeys.filter((k) => k !== cssVar);
			continue;
		}
		root.style.setProperty(cssVar, String(value));
		if (!appliedTokenKeys.includes(cssVar)) {
			appliedTokenKeys.push(cssVar);
		}
	}

	if (appliedTokenKeys.length > 0) {
		root.setAttribute(APPLIED_TOKEN_ATTR, "1");
	} else {
		root.removeAttribute(APPLIED_TOKEN_ATTR);
	}
}

/**
 * Remove runtime CSS injection and inline token overrides.
 * Build-time system theme stylesheet is left in place.
 */
export function clearCustomTheme() {
	if (typeof document === "undefined") return;

	const style = document.getElementById(CUSTOM_THEME_STYLE_ID);
	if (style) style.remove();

	const root = document.documentElement;
	for (const cssVar of appliedTokenKeys) {
		root.style.removeProperty(cssVar);
	}
	appliedTokenKeys = [];
	root.removeAttribute(APPLIED_TOKEN_ATTR);
}

export { TOKEN_ALIASES };
